import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoadingScreen from './components/common/LoadingScreen';
import ScrollToTop from './components/common/ScrollToTop';

// Layout
import Layout from './components/layout/Layout';
import MainLayout from './components/layout/MainLayout';

// Pages
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import TemplatesPage from './pages/TemplatesPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DocumentEditorPage from './pages/DocumentEditorPage';
import DocumentUploadPage from './pages/DocumentUploadPage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import ProfilePage from './pages/ProfilePage';
import BlogPage from './pages/BlogPage';
import DocumentationPage from './pages/DocumentationPage';
import SupportPage from './pages/SupportPage';
import FAQPage from './pages/FAQPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CareersPage from './pages/CareersPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import CookiesPage from './pages/CookiesPage';

// Document components
import DocumentPage from './components/documents/DocumentPage';

// Placeholder pages - These would be created later
const DocumentsPage = () => <div>Documents Page</div>;
const DisclaimerPage = () => <div>Disclaimer Page</div>;
const NotFoundPage = () => <div>404 - Page Not Found</div>;

// Auth guard component
interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  // Use the useAuth hook to check authentication status
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Show loading indicator while authentication status is being checked
  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" state={{ from: location }} />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Landing page - standalone with its own header and footer */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Auth pages - no layout */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Public pages with direct header inclusion */}
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            
            {/* MainLayout for placeholder pages */}
            <Route element={<MainLayout />}>
              <Route path="/disclaimer" element={<DisclaimerPage />} />
            </Route>
            
            {/* Protected routes - with Layout */}
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <PrivateRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/templates" 
              element={
                <PrivateRoute>
                  <Layout>
                    <TemplatesPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/templates/:id" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentEditorPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/templates/new" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentEditorPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/templates/edit/:id" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentEditorPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/documents" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentsPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/documents/:documentId" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/documents/new" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/documents/upload" 
              element={
                <PrivateRoute>
                  <Layout>
                    <DocumentUploadPage />
                  </Layout>
                </PrivateRoute>
              } 
            />
            
            {/* 404 route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
